import React, { useEffect, useMemo, useState } from 'react';
import {
  FileText, Download, Pencil, Sparkles, RotateCcw, Loader2, X,
  Bold, List, Heading2, CheckCircle2,
} from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

// Simple markdown-lite → HTML for the live preview.
function mdToHtml(md, variables = {}) {
  const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let out = [];
  let inList = false;
  (md || '').split('\n').forEach((raw) => {
    let line = esc(raw);
    line = line.replace(/\{\{(\w+)\}\}/g, (_, k) => `<span class="px-1 rounded bg-[#EDE8FF] text-[#4F46E5] font-semibold">${esc(String(variables[k] ?? '')) || `{{${k}}}`}</span>`);
    line = line.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
    if (line.startsWith('## ')) {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<h3 class="text-[13px] font-bold text-[#4F46E5] mt-3 mb-1">${line.slice(3)}</h3>`);
    } else if (line.trim().startsWith('- ')) {
      if (!inList) { out.push('<ul class="list-disc pl-5 space-y-0.5">'); inList = true; }
      out.push(`<li>${line.trim().slice(2)}</li>`);
    } else if (!line.trim()) {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push('<div class="h-2"></div>');
    } else {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push(`<p>${line}</p>`);
    }
  });
  if (inList) out.push('</ul>');
  return out.join('');
}

function EditorDialog({ doc, onClose, onSaved }) {
  const [content, setContent] = useState('');
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiActivity, setAiActivity] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const taRef = React.useRef(null);

  useEffect(() => {
    if (!doc) return;
    api.get(`/qualiopi/doc-templates/${doc.id}`).then(({ data }) => {
      setDetail(data);
      setContent(data.content);
    }).catch(() => toast({ title: 'Erreur', description: 'Chargement du modèle impossible' }));
  }, [doc]);

  const insert = (before, after = '') => {
    const ta = taRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e } = ta;
    const sel = content.slice(s, e) || 'texte';
    setContent(content.slice(0, s) + before + sel + after + content.slice(e));
    setTimeout(() => ta.focus(), 0);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/qualiopi/doc-templates/${doc.id}`, { content });
      toast({ title: 'Modèle enregistré', description: doc.title });
      onSaved();
      onClose();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };

  const reset = async () => {
    try {
      const { data } = await api.post(`/qualiopi/doc-templates/${doc.id}/reset`);
      setContent(data.content);
      toast({ title: 'Modèle réinitialisé au contenu par défaut' });
      onSaved();
    } catch { toast({ title: 'Erreur', description: 'Échec de la réinitialisation' }); }
  };

  const aiAdapt = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/qualiopi/doc-templates/${doc.id}/ai-adapt`, {
        activity: aiActivity, content,
      });
      if (data.content) {
        setContent(data.content);
        setAiOpen(false);
        toast({ title: '✨ Modèle adapté par l\'IA', description: 'Relisez puis enregistrez.' });
      }
    } catch (err) {
      toast({ title: 'Erreur IA', description: err?.response?.data?.detail || 'Échec' });
    } finally { setAiLoading(false); }
  };

  if (!doc) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="qualiopi-doc-editor">
        <DialogHeader>
          <DialogTitle className="text-[16px]">{doc.title}</DialogTitle>
          <DialogDescription className="text-[12px]">
            Critère {doc.criterion} · Indicateur(s) {doc.indicators.join(', ')} — les variables {'{{...}}'} sont remplacées automatiquement dans le PDF.
          </DialogDescription>
        </DialogHeader>
        {!detail ? (
          <div className="py-10 text-center text-[#8E8FA5]"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : (
          <>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button onClick={() => insert('## ')} title="Titre" data-testid="doc-editor-h2" className="h-8 px-2 rounded-lg border border-[#EFF0F6] text-[#4A4B63] hover:bg-[#FAFAFE] flex items-center gap-1 text-[11px] font-semibold"><Heading2 className="w-3.5 h-3.5" /> Titre</button>
              <button onClick={() => insert('**', '**')} title="Gras" data-testid="doc-editor-bold" className="h-8 w-8 rounded-lg border border-[#EFF0F6] text-[#4A4B63] hover:bg-[#FAFAFE] flex items-center justify-center"><Bold className="w-3.5 h-3.5" /></button>
              <button onClick={() => insert('- ')} title="Liste" data-testid="doc-editor-list" className="h-8 w-8 rounded-lg border border-[#EFF0F6] text-[#4A4B63] hover:bg-[#FAFAFE] flex items-center justify-center"><List className="w-3.5 h-3.5" /></button>
              <div className="w-px h-5 bg-[#EFF0F6] mx-1" />
              {(detail.variable_keys || []).map((k) => (
                <button key={k} onClick={() => insert(`{{${k}}}`)} className="h-7 px-2 rounded-full bg-[#EDE8FF] text-[#4F46E5] text-[10.5px] font-semibold hover:bg-[#E0D8FF]" title={detail.variables?.[k]}>
                  {`{{${k}}}`}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 flex-1 min-h-0">
              <textarea
                ref={taRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                data-testid="doc-editor-textarea"
                className="w-full h-[45vh] p-3 rounded-xl border border-[#EFF0F6] text-[12.5px] font-mono leading-relaxed outline-none focus:ring-2 focus:ring-[#7C5CFC] resize-none"
              />
              <div
                className="h-[45vh] overflow-y-auto p-4 rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] text-[12px] text-[#1A1B2E]"
                data-testid="doc-editor-preview"
                dangerouslySetInnerHTML={{ __html: mdToHtml(content, detail.variables) }}
              />
            </div>
            {aiOpen && (
              <div className="p-3 rounded-xl bg-[#F3E8FF] border border-[#D8B4FE] flex items-center gap-2" data-testid="doc-editor-ai-panel">
                <Sparkles className="w-4 h-4 text-[#7C3AED] shrink-0" />
                <input
                  value={aiActivity}
                  onChange={(e) => setAiActivity(e.target.value)}
                  placeholder="Décrivez votre activité (ex : formations en sécurité incendie pour les ERP)…"
                  data-testid="doc-editor-ai-activity"
                  className="flex-1 h-9 px-3 rounded-lg border border-[#D8B4FE] text-[12.5px] outline-none bg-white"
                />
                <button onClick={aiAdapt} disabled={aiLoading} data-testid="doc-editor-ai-run" className="h-9 px-4 rounded-lg bg-[#7C3AED] text-white text-[12px] font-semibold disabled:opacity-60 flex items-center gap-1.5">
                  {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Adapter
                </button>
                <button onClick={() => setAiOpen(false)} className="text-[#8E8FA5] hover:text-[#1A1B2E]"><X className="w-4 h-4" /></button>
              </div>
            )}
            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="flex items-center gap-2">
                <button onClick={() => setAiOpen((v) => !v)} data-testid="doc-editor-ai-toggle" className="h-9 px-3 rounded-lg border border-[#D8B4FE] text-[#7C3AED] bg-white hover:bg-[#F3E8FF] text-[12px] font-semibold flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Adapter avec l'IA
                </button>
                <button onClick={reset} data-testid="doc-editor-reset" className="h-9 px-3 rounded-lg border border-[#EFF0F6] text-[#6B6C85] hover:bg-[#FAFAFE] text-[12px] font-semibold flex items-center gap-1.5">
                  <RotateCcw className="w-3.5 h-3.5" /> Réinitialiser
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="h-9 px-4 rounded-lg border border-[#EFF0F6] text-[#4A4B63] text-[12.5px] font-semibold">Annuler</button>
                <button onClick={save} disabled={saving} data-testid="doc-editor-save" className="h-9 px-5 rounded-lg brand-gradient text-white text-[12.5px] font-semibold disabled:opacity-60 flex items-center gap-1.5">
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Enregistrer
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function QualiopiDocTemplates() {
  const [templates, setTemplates] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = () => {
    api.get('/qualiopi/doc-templates')
      .then(({ data }) => { setTemplates(data.templates || []); setCounts(data.counts || {}); })
      .catch(() => toast({ title: 'Erreur', description: 'Chargement des modèles impossible' }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const grouped = useMemo(() => {
    const g = new Map();
    templates.forEach((t) => {
      if (!g.has(t.criterion)) g.set(t.criterion, { name: t.criterion_name, items: [] });
      g.get(t.criterion).items.push(t);
    });
    return Array.from(g.entries()).sort((a, b) => a[0] - b[0]);
  }, [templates]);

  const downloadPdf = async (t) => {
    setDownloading(t.id);
    try {
      const res = await api.get(`/qualiopi/doc-templates/${t.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `qualiopi_${t.id}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch { toast({ title: 'Erreur', description: 'Génération du PDF impossible' }); }
    finally { setDownloading(null); }
  };

  if (loading) {
    return <div className="py-12 text-center text-[#8E8FA5]"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  }

  const totalCustom = templates.filter((t) => t.customized).length;

  return (
    <div className="space-y-6" data-testid="qualiopi-doc-templates">
      <div className="soft-card p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-[16px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Modèles de documents Qualiopi
          </h2>
          <p className="text-[12.5px] text-[#6B6C85] mt-0.5">
            {templates.length} modèles préremplis couvrant les 7 critères. Personnalisez-les, adaptez-les avec l'IA et exportez-les en PDF avec votre en-tête — prêts pour l'audit.
          </p>
        </div>
        <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-[#E6FBF7] text-[#0F766E]" data-testid="doc-templates-custom-count">
          <CheckCircle2 className="w-3.5 h-3.5" /> {totalCustom} / {templates.length} personnalisés
        </div>
      </div>

      {grouped.map(([crit, g]) => (
        <section key={crit} data-testid={`doc-templates-criterion-${crit}`}>
          <div className="flex items-center gap-2 mb-2.5">
            <span className="w-7 h-7 rounded-lg brand-gradient text-white text-[12px] font-bold flex items-center justify-center">{crit}</span>
            <h3 className="text-[14px] font-bold text-[#1A1B2E]">{g.name}</h3>
            <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-[#F3F4F8] text-[#8E8FA5]">
              {counts[crit]?.customized || 0}/{counts[crit]?.total || g.items.length} personnalisés
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {g.items.map((t) => (
              <div key={t.id} className="soft-card p-4 flex flex-col gap-2" data-testid={`doc-template-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="w-9 h-9 rounded-lg bg-[#EDE8FF] flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-[#4F46E5]" />
                  </div>
                  {t.customized ? (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#E6FBF7] text-[#0F766E]">Personnalisé</span>
                  ) : (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#F3F4F8] text-[#8E8FA5]">Modèle</span>
                  )}
                </div>
                <div>
                  <div className="text-[13px] font-bold text-[#1A1B2E] leading-snug">{t.title}</div>
                  <div className="text-[11.5px] text-[#8E8FA5] mt-0.5 line-clamp-2">{t.description}</div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {t.indicators.map((i) => (
                    <span key={i} className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5]">Ind. {i}</span>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 mt-auto pt-1">
                  <button
                    onClick={() => setEditing(t)}
                    data-testid={`doc-template-edit-${t.id}`}
                    className="flex-1 h-8 rounded-lg border border-[#EFF0F6] text-[#4A4B63] hover:bg-[#FAFAFE] text-[11.5px] font-semibold flex items-center justify-center gap-1.5"
                  >
                    <Pencil className="w-3 h-3" /> Personnaliser
                  </button>
                  <button
                    onClick={() => downloadPdf(t)}
                    disabled={downloading === t.id}
                    data-testid={`doc-template-pdf-${t.id}`}
                    title="Exporter en PDF avec votre en-tête"
                    className="h-8 px-3 rounded-lg brand-gradient text-white text-[11.5px] font-semibold flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {downloading === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {editing && <EditorDialog doc={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
